import React, { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      const t = (key: string) => i18n.t(key, { ns: 'companies' });
      
      return this.props.fallback || (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-bg-secondary rounded-3xl border border-danger/20 text-center">
          <div className="w-16 h-16 bg-danger/10 text-danger rounded-2xl flex items-center justify-center mb-6">
            <span className="text-2xl font-bold">!</span>
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">{t('common.error.title')}</h2>
          <p className="text-text-secondary max-w-md mb-6">
            {t('common.error.description')}
            <span className="block mt-2 p-2 bg-black/5 rounded text-[10px] font-mono text-danger overflow-auto max-h-24">
              {this.state.error?.message}
            </span>
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-text-primary text-bg-primary rounded-xl font-bold text-xs uppercase ltr:tracking-widest rtl:tracking-normal hover:bg-black transition-all"
          >
            {t('common.error.reset')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
